/**
 * External dependencies
 */
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import {
  Card,
  CardContent,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';
import { Checkbox } from '@art-of-hospitality-solutions-llc/ui-kit/components/checkbox';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/dialog';
import { toastSuccess } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';

/**
 * Internal dependencies
 */
import Breadcrumbs from '@Components/Breadcrumbs';
import { useParams, useNavigate } from 'react-router-dom';
import { useSoul, soulKeys, useSouls } from '@Hooks/useSouls';
import { useAgentProfile, agentProfileKeys } from '@Hooks/useAgentProfile';
import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import service from '@Service';
import { AVAILABLE_TOOLS } from '@Constants/aiTools';
import SoulExamplesCard from './SoulExamplesCard';
import AgentOperationsCard from './AgentOperationsCard';
import AgentContactsCard from './AgentContactsCard';

const inputClass =
  'border-input flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]';
const textareaClass =
  'border-input flex min-h-16 w-full rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]';

export default function SoulDetails() {
  const { soulId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: soul, status } = useSoul({ soulId });
  const { data: agentProfile } = useAgentProfile({ soulId });
  const { data: allSouls } = useSouls();
  const parentCandidates = (allSouls ?? []).filter(
    (s) => s.id !== soulId && !s.parentSoulId,
  );

  const [editing, setEditing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !soulId) return;
    const sanitized = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const sanitizedFile = new File([file], sanitized, { type: file.type });
    const formData = new FormData();
    formData.append('images', sanitizedFile);
    formData.append('assetTags', JSON.stringify([{ name: 'Agent Avatar' }]));
    setIsUploadingImage(true);
    try {
      await service.uploadSoulImage(soulId, formData);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: soulKeys.detail(soulId) }),
        queryClient.invalidateQueries({ queryKey: agentProfileKeys.detail(soulId) }),
      ]);
      toastSuccess('Avatar updated.');
    } catch (e: any) {
      toastSuccess(`Upload failed: ${e?.message ?? e}`);
    } finally {
      setIsUploadingImage(false);
      e.target.value = '';
    }
  };

  // Edit form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [model, setModel] = useState('');
  const [enabledTools, setEnabledTools] = useState<string[]>([]);
  const [temperature, setTemperature] = useState('');
  const [maxSteps, setMaxSteps] = useState('');
  const [maxTokens, setMaxTokens] = useState('');
  const [thinkingBudget, setThinkingBudget] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [parentSoulId, setParentSoulId] = useState<string>('');
  const [formError, setFormError] = useState<string | null>(null);

  const startEditing = useCallback(() => {
    if (!soul) return;
    setName(soul.name);
    setDescription(soul.description ?? '');
    setSystemPrompt(soul.systemPrompt);
    setModel(soul.model ?? '');
    // Drop any tools no longer in the registry. They have no checkbox in the
    // grid, so they'd otherwise ride along invisibly and fail save validation.
    setEnabledTools(
      (soul.enabledTools ?? []).filter((tool) =>
        (AVAILABLE_TOOLS as readonly string[]).includes(tool),
      ),
    );
    setTemperature(soul.temperature?.toString() ?? '');
    setMaxSteps(soul.maxSteps?.toString() ?? '');
    setMaxTokens(soul.maxTokens?.toString() ?? '');
    setThinkingBudget(soul.thinkingBudget?.toString() ?? '');
    setIsDefault(soul.isDefault);
    setParentSoulId(soul.parentSoulId ?? '');
    setFormError(null);
    setEditing(true);
  }, [soul]);

  const cancelEditing = () => setEditing(false);

  const toggleTool = (tool: string, checked: boolean) => {
    setEnabledTools((prev) =>
      checked ? [...prev, tool] : prev.filter((t) => t !== tool),
    );
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!soulId) return;
    setFormError(null);

    if (!name.trim()) {
      setFormError('Name is required.');
      return;
    }
    if (!systemPrompt.trim()) {
      setFormError('System prompt is required.');
      return;
    }

    setIsSubmitting(true);
    try {
      await service.updateSoul(soulId, {
        name: name.trim(),
        systemPrompt: systemPrompt.trim(),
        description: description.trim() || null,
        model: model.trim() || null,
        enabledTools: enabledTools.length ? enabledTools : null,
        temperature: temperature ? Number(temperature) : null,
        maxSteps: maxSteps ? Number(maxSteps) : null,
        maxTokens: maxTokens ? Number(maxTokens) : null,
        thinkingBudget: thinkingBudget ? Number(thinkingBudget) : null,
        isDefault,
        parentSoulId: parentSoulId || null,
      });
      await queryClient.invalidateQueries({ queryKey: soulKeys.all });
      await queryClient.invalidateQueries({
        queryKey: soulKeys.detail(soulId),
      });
      toastSuccess('Soul updated successfully.');
      setEditing(false);
    } catch (err) {
      console.error('Error updating soul:', err);
      setFormError('Unable to update soul. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = useCallback(async () => {
    if (!soulId) return;
    setIsDeleting(true);
    try {
      await service.deleteSoul(soulId);
      await queryClient.invalidateQueries({ queryKey: soulKeys.all });
      toastSuccess('Soul deleted.');
      navigate('/agents');
    } catch (error) {
      console.error('Error deleting soul:', error);
    } finally {
      setIsDeleting(false);
      setDeleteOpen(false);
    }
  }, [soulId, queryClient, navigate]);

  if (status === 'pending' || !soul) {
    return <div>Loading...</div>;
  }

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'AI', href: '/agents' },
          { name: 'Agents', href: '/agents' },
          { name: soul.name, href: `/agents/${soulId}` },
        ]}
      />

      <div className="mt-8 mb-4 flex items-center gap-4">
        <label
          className="group relative shrink-0 cursor-pointer"
          title="Click to upload avatar"
        >
          <div className="h-16 w-16 overflow-hidden rounded-full bg-zinc-100 ring-2 ring-white shadow">
            {agentProfile?.avatarUrl ? (
              <img
                src={agentProfile.avatarUrl}
                alt={soul.name}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-xl font-bold text-zinc-400">
                {soul.name.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 transition group-hover:opacity-100">
            {isUploadingImage ? (
              <span className="text-xs text-white">…</span>
            ) : (
              <span className="text-xs text-white">Edit</span>
            )}
          </div>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageUpload}
            disabled={isUploadingImage}
          />
        </label>

        <div className="flex items-end gap-2">
          <Heading>{soul.name}</Heading>
          {soul.isDefault && (
            <Badge variant="secondary" className="mb-1">
              Default
            </Badge>
          )}
        </div>

        <div className="ml-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/agents/${soulId}`)}
          >
            View Agent Desk
          </Button>
        </div>
      </div>

      {editing ? (
        /* ─── Edit Mode ─── */
        <form onSubmit={handleSave}>
          {formError && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {formError}
            </div>
          )}

          <div className="grid grid-cols-1 gap-8 xl:grid-cols-3">
            <div className="flex flex-col space-y-8 xl:col-span-2">
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <Heading level="h3">Details</Heading>
                  <div className="flex items-center space-x-2">
                    <Button
                      type="submit"
                      variant="primary"
                      size="sm"
                      loading={isSubmitting}
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={cancelEditing}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
                <Card>
                  <CardContent>
                    <div className="space-y-4 py-2">
                      <div>
                        <label className="text-sm font-bold text-zinc-500">
                          Name
                        </label>
                        <input
                          className={inputClass}
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-bold text-zinc-500">
                          Description
                        </label>
                        <textarea
                          className={textareaClass}
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-bold text-zinc-500">
                          Parent Agent
                        </label>
                        <select
                          className={inputClass}
                          value={parentSoulId}
                          onChange={(e) => setParentSoulId(e.target.value)}
                        >
                          <option value="">— None (top-level agent) —</option>
                          {parentCandidates.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-sm font-bold text-zinc-500">
                          Model
                        </label>
                        <input
                          className={inputClass}
                          placeholder="Leave blank for default"
                          value={model}
                          onChange={(e) => setModel(e.target.value)}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                        <div>
                          <label className="text-sm font-bold text-zinc-500">
                            Temperature
                          </label>
                          <input
                            className={inputClass}
                            type="number"
                            step="0.1"
                            min="0"
                            max="2"
                            placeholder="0-2"
                            value={temperature}
                            onChange={(e) => setTemperature(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="text-sm font-bold text-zinc-500">
                            Max Steps
                          </label>
                          <input
                            className={inputClass}
                            type="number"
                            min="1"
                            max="20"
                            placeholder="1-20"
                            value={maxSteps}
                            onChange={(e) => setMaxSteps(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="text-sm font-bold text-zinc-500">
                            Max Tokens
                          </label>
                          <input
                            className={inputClass}
                            type="number"
                            min="1"
                            max="32768"
                            placeholder="1-32768"
                            value={maxTokens}
                            onChange={(e) => setMaxTokens(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="text-sm font-bold text-zinc-500">
                            Thinking Budget
                          </label>
                          <input
                            className={inputClass}
                            type="number"
                            min="1024"
                            max="128000"
                            placeholder="1024-128000"
                            value={thinkingBudget}
                            onChange={(e) => setThinkingBudget(e.target.value)}
                          />
                        </div>
                      </div>
                      <label className="flex items-center gap-2 text-sm font-bold text-zinc-500">
                        <Checkbox
                          checked={isDefault}
                          onCheckedChange={(checked) =>
                            setIsDefault(checked === true)
                          }
                        />
                        Default Agent
                      </label>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div>
                <Heading level="h3" className="mb-2">
                  System Prompt
                </Heading>
                <Card>
                  <CardContent>
                    <textarea
                      className={`${textareaClass} min-h-64`}
                      value={systemPrompt}
                      onChange={(e) => setSystemPrompt(e.target.value)}
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            <div className="flex flex-col space-y-8">
              <div>
                <Heading level="h3" className="mb-2">
                  Enabled Tools
                </Heading>
                <Card>
                  <CardContent>
                    <div className="space-y-2 py-2">
                      {AVAILABLE_TOOLS.map((tool) => (
                        <label
                          key={tool}
                          className="flex items-center gap-2 text-sm"
                        >
                          <Checkbox
                            checked={enabledTools.includes(tool)}
                            onCheckedChange={(checked) =>
                              toggleTool(tool, checked === true)
                            }
                          />
                          {tool}
                        </label>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </form>
      ) : (
        /* ─── View Mode ─── */
        <div className="grid grid-cols-1 gap-8 xl:grid-cols-3">
          <div className="flex flex-col space-y-8 xl:col-span-2">
            <div>
              <div className="mb-2 flex items-center justify-between">
                <Heading level="h3">Details</Heading>
                <div className="flex items-center space-x-2">
                  <Button variant="outline" size="sm" onClick={startEditing}>
                    Edit
                  </Button>
                  <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                    <DialogTrigger asChild>
                      <Button variant="dangerLink" size="sm">
                        Delete
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>Delete {soul.name}?</DialogTitle>
                      </DialogHeader>
                      <p className="text-sm text-zinc-600">
                        This will permanently delete this soul configuration.
                        This action cannot be undone.
                      </p>
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button variant="link">Cancel</Button>
                        </DialogClose>
                        <Button
                          variant="destructive"
                          onClick={handleDelete}
                          loading={isDeleting}
                        >
                          Delete
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
              <Card>
                <CardContent>
                  <div className="space-y-4 py-2">
                    <div>
                      <div className="text-sm font-bold text-zinc-500">
                        Name
                      </div>
                      <div>{soul.name}</div>
                    </div>
                    {soul.description && (
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Description
                        </div>
                        <div>{soul.description}</div>
                      </div>
                    )}
                    <div>
                      <div className="text-sm font-bold text-zinc-500">
                        Model
                      </div>
                      <div>
                        {soul.model ? (
                          <Badge variant="outline">{soul.model}</Badge>
                        ) : (
                          <span className="text-zinc-400">Using default</span>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Temperature
                        </div>
                        <div>
                          {soul.temperature !== null ? (
                            soul.temperature
                          ) : (
                            <span className="text-zinc-400">default</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Max Steps
                        </div>
                        <div>
                          {soul.maxSteps !== null ? (
                            soul.maxSteps
                          ) : (
                            <span className="text-zinc-400">default</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Max Tokens
                        </div>
                        <div>
                          {soul.maxTokens !== null ? (
                            soul.maxTokens
                          ) : (
                            <span className="text-zinc-400">default</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Thinking Budget
                        </div>
                        <div>
                          {soul.thinkingBudget !== null ? (
                            soul.thinkingBudget.toLocaleString()
                          ) : (
                            <span className="text-zinc-400">disabled</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div>
              <Heading level="h3" className="mb-2">
                System Prompt
              </Heading>
              <Card>
                <CardContent>
                  <pre className="text-sm leading-relaxed whitespace-pre-wrap">
                    {soul.systemPrompt}
                  </pre>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="flex flex-col space-y-8">
            <div>
              <Heading level="h3" className="mb-2">
                Enabled Tools
              </Heading>
              <Card>
                <CardContent>
                  {soul.enabledTools?.length ? (
                    <div className="flex flex-wrap gap-2 py-2">
                      {soul.enabledTools.map((tool) => (
                        <Badge key={tool} variant="outline">
                          {tool}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <div className="py-4 text-sm text-zinc-500">
                      No tools enabled for this soul.
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <div>
              <Heading level="h3" className="mb-2">
                Metadata
              </Heading>
              <Card>
                <CardContent>
                  <div className="space-y-2 py-2 text-sm">
                    <div>
                      <span className="font-bold text-zinc-500">Created:</span>{' '}
                      {new Date(soul.createdAt).toLocaleString()}
                    </div>
                    <div>
                      <span className="font-bold text-zinc-500">Updated:</span>{' '}
                      {new Date(soul.updatedAt).toLocaleString()}
                    </div>
                    <div>
                      <span className="font-bold text-zinc-500">ID:</span>{' '}
                      <code className="text-xs">{soul.id}</code>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6">
        <AgentOperationsCard soulId={soul.id} />
      </div>
      <div className="mt-6">
        <AgentContactsCard soulId={soul.id} />
      </div>
      <div className="mt-6">
        <SoulExamplesCard soulId={soul.id} />
      </div>
    </div>
  );
}
